import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { QuoteDrawerProvider } from "@/contexts/QuoteDrawerContext";
import { BlueprintProvider } from "@/contexts/BlueprintContext";
import { SettingsProvider } from "@/contexts/SettingsContext";
import { BackToTop, CookieBanner, PageTransition } from "@/components/GlobalUI";
import "@/App.css";
import { lazy, Suspense, useEffect } from "react";

// Layout - Keep eager loaded for instant header/footer
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import QuickQuoteDrawer from "@/components/layout/QuickQuoteDrawer";
import SEOHead, { StructuredData } from "@/components/SEOHead";
import AuraChat from "@/components/AuraChat";
import WhatsAppWidget from "@/components/WhatsAppWidget";

// Skeleton components for loading states
import { PageSkeleton, ServiceSkeleton, ProjectsSkeleton, ContactSkeleton } from "@/components/Skeletons";

// ============================================
// LAZY LOADED PAGES - Route-Based Code Splitting
// ============================================

// Critical path - Home page (could be eager if needed)
const HomePage = lazy(() => import(/* webpackChunkName: "home" */ "@/pages/Home"));

// Services pages
const ServicesPage = lazy(() => import(/* webpackChunkName: "services" */ "@/pages/Services"));
const ServiceDetailPage = lazy(() => import(/* webpackChunkName: "service-detail" */ "@/pages/ServiceDetail"));
const ServicePillarPage = lazy(() => import(/* webpackChunkName: "service-pillar" */ "@/pages/ServicePillar"));
const BespokeJoineryPage = lazy(() => import(/* webpackChunkName: "bespoke-joinery" */ "@/pages/BespokeJoinery"));

// Projects pages
const ProjectsPage = lazy(() => import(/* webpackChunkName: "projects" */ "@/pages/Projects"));
const ProjectDetailPage = lazy(() => import(/* webpackChunkName: "project-detail" */ "@/pages/ProjectDetail"));
const ProjectStoriesPage = lazy(() => import(/* webpackChunkName: "project-stories" */ "@/pages/ProjectStories"));

// Sectors pages
const SectorsPage = lazy(() => import(/* webpackChunkName: "sectors" */ "@/pages/Sectors"));
const SectorDetailPage = lazy(() => import(/* webpackChunkName: "sector-detail" */ "@/pages/SectorDetail"));

// Core pages
const AboutPage = lazy(() => import(/* webpackChunkName: "about" */ "@/pages/About"));
const ContactPage = lazy(() => import(/* webpackChunkName: "contact" */ "@/pages/Contact"));
const HowWeWorkPage = lazy(() => import(/* webpackChunkName: "how-we-work" */ "@/pages/HowWeWork"));

// Factory & Capabilities
const OurFactoryPage = lazy(() => import(/* webpackChunkName: "factory" */ "@/pages/OurFactory"));
const FactoryPage = lazy(() => import(/* webpackChunkName: "factory" */ "@/pages/Factory"));
const FabricationCapabilitiesPage = lazy(() => import(/* webpackChunkName: "fabrication" */ "@/pages/FabricationCapabilities"));
const MaterialsPage = lazy(() => import(/* webpackChunkName: "materials" */ "@/pages/Materials"));
const CapabilitiesPage = lazy(() => import(/* webpackChunkName: "capabilities" */ "@/pages/Capabilities"));
const CapabilityPage = lazy(() => import(/* webpackChunkName: "capability-detail" */ "@/components/capabilities/CapabilityPage"));
const FurnishingLifestylePage = lazy(() => import(/* webpackChunkName: "furnishing-lifestyle" */ "@/pages/FurnishingLifestyle"));

// Industries & Applications
const IndustriesPage = lazy(() => import(/* webpackChunkName: "industries" */ "@/pages/Industries"));
const ApplicationPage = lazy(() => import(/* webpackChunkName: "application" */ "@/pages/ApplicationPage"));

// Lead generation pages
const ForArchitectsPage = lazy(() => import(/* webpackChunkName: "for-architects" */ "@/pages/ForArchitects"));
const ForContractorsPage = lazy(() => import(/* webpackChunkName: "for-contractors" */ "@/pages/ForContractors"));
const ForRetailBrandsPage = lazy(() => import(/* webpackChunkName: "for-retail" */ "@/pages/ForRetailBrands"));

// Trust & SEO pages
const AreasWeServePage = lazy(() => import(/* webpackChunkName: "areas" */ "@/pages/AreasWeServe"));
const QualityHSEPage = lazy(() => import(/* webpackChunkName: "quality-hse" */ "@/pages/QualityHSE"));
const AfterCarePage = lazy(() => import(/* webpackChunkName: "aftercare" */ "@/pages/AfterCareWarranty"));
const AuthorityApprovalsPage = lazy(() => import(/* webpackChunkName: "approvals" */ "@/pages/AuthorityApprovals"));

// Content pages
const InsightsPage = lazy(() => import(/* webpackChunkName: "insights" */ "@/pages/Insights").then(m => ({ default: m.default })));
const InsightArticlePage = lazy(() => import(/* webpackChunkName: "insight-article" */ "@/pages/Insights").then(m => ({ default: m.InsightArticlePage })));
const ResourcesPage = lazy(() => import(/* webpackChunkName: "resources" */ "@/pages/Resources"));

// Utility pages
const RequestQuotePage = lazy(() => import(/* webpackChunkName: "quote" */ "@/pages/RequestQuote"));
const VendorRegistration = lazy(() => import(/* webpackChunkName: "vendor" */ "@/pages/VendorRegistration"));
const JoinerySubPage = lazy(() => import(/* webpackChunkName: "joinery-sub" */ "@/pages/JoinerySubPage"));
const PrastaraOS = lazy(() => import(/* webpackChunkName: "prastara-os" */ "@/pages/PrastaraOS"));
const ToolsPage = lazy(() => import(/* webpackChunkName: "tools" */ "@/pages/Tools"));
const ProfessionalsPage = lazy(() => import(/* webpackChunkName: "professionals" */ "@/pages/Professionals"));
const ProfessionalsResourcesPage = lazy(() => import(/* webpackChunkName: "professionals" */ "@/pages/ProfessionalsResources"));
const ProfessionalsToolsPage = lazy(() => import(/* webpackChunkName: "professionals" */ "@/pages/ProfessionalsTools"));
const ProfessionalSubmissionPage = lazy(() => import(/* webpackChunkName: "professionals" */ "@/pages/ProfessionalSubmission"));

// Legal pages
const PrivacyPolicyPage = lazy(() => import(/* webpackChunkName: "privacy" */ "@/pages/PrivacyPolicy"));
const TermsPage = lazy(() => import(/* webpackChunkName: "terms" */ "@/pages/Terms"));

// Admin pages - separate chunk
const AdminLogin = lazy(() => import(/* webpackChunkName: "admin" */ "@/pages/admin/AdminLogin"));
const AdminDashboard = lazy(() => import(/* webpackChunkName: "admin" */ "@/pages/admin/AdminDashboard").then(m => ({ default: m.default })));
const AdminLayout = lazy(() => import(/* webpackChunkName: "admin" */ "@/pages/admin/AdminDashboard").then(m => ({ default: m.AdminLayout })));
const AdminProjects = lazy(() => import(/* webpackChunkName: "admin" */ "@/pages/admin/AdminProjects"));
const AdminServices = lazy(() => import(/* webpackChunkName: "admin" */ "@/pages/admin/AdminServices"));
const AdminSectors = lazy(() => import(/* webpackChunkName: "admin" */ "@/pages/admin/AdminSectors"));
const AdminInquiries = lazy(() => import(/* webpackChunkName: "admin" */ "@/pages/admin/AdminInquiries"));
const AdminActivityLog = lazy(() => import(/* webpackChunkName: "admin" */ "@/pages/admin/AdminActivityLog"));
const AdminTestimonials = lazy(() => import(/* webpackChunkName: "admin" */ "@/pages/admin/AdminTestimonials"));
const AdminSettings = lazy(() => import(/* webpackChunkName: "admin" */ "@/pages/admin/AdminSettings"));
const AdminTeam = lazy(() => import(/* webpackChunkName: "admin" */ "@/pages/admin/AdminTeam"));
const AdminResources = lazy(() => import(/* webpackChunkName: "admin" */ "@/pages/admin/AdminResources"));
const AdminBlog = lazy(() => import(/* webpackChunkName: "admin" */ "@/pages/admin/AdminBlog"));
const AdminBeforeAfter = lazy(() => import(/* webpackChunkName: "admin" */ "@/pages/admin/AdminBeforeAfter"));
const AdminProjectStories = lazy(() => import(/* webpackChunkName: "admin" */ "@/pages/admin/AdminProjectStories"));
const AdminGoogleReviews = lazy(() => import(/* webpackChunkName: "admin" */ "@/pages/admin/AdminGoogleReviews"));
const AdminSEO = lazy(() => import(/* webpackChunkName: "admin" */ "@/pages/admin/AdminSEO"));
const AdminImages = lazy(() => import(/* webpackChunkName: "admin" */ "@/pages/admin/AdminImages"));
const AdminMaterials = lazy(() => import(/* webpackChunkName: "admin" */ "@/pages/admin/AdminMaterials"));
const NotFound = lazy(() => import(/* webpackChunkName: "notfound" */ "@/pages/NotFound"));
const DesignSystemPage = lazy(() => import(/* webpackChunkName: "design-system" */ "@/pages/DesignSystem"));


// ============================================
// SCROLL TO TOP COMPONENT
// ============================================
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

// ============================================
// PROTECTED ROUTE WRAPPER
// ============================================
function ProtectedRoute({ children }) {
  const { admin, loading } = useAuth();
  if (loading) return <PageSkeleton />;
  if (!admin) return <Navigate to="/admin/login" replace />;
  return children;
}

// ============================================
// INDUSTRY REDIRECT (/industries/:slug → /sectors/:slug)
// ============================================
function IndustryRedirect() {
  const { slug } = useParams();
  const redirects = new Set(['retail-chains', 'hospitality-fnb', 'residential', 'offices-commercial', 'supermarkets', 'jewellery-luxury', 'kiosks']);
  return redirects.has(slug) ? <Navigate to={`/sectors/${slug}`} replace /> : <NotFound />;
}

// ============================================
// SUSPENSE WRAPPERS WITH SPECIFIC SKELETONS
// ============================================
function SuspenseWrapper({ children, fallback = <PageSkeleton /> }) {
  return <Suspense fallback={fallback}>{children}</Suspense>;
}

// ============================================
// PUBLIC LAYOUT WRAPPER
// ============================================
function PublicLayout({ children, skeleton = <PageSkeleton /> }) {
  const { pathname } = useLocation();
  const isAdmin = pathname.startsWith('/admin');
  if (isAdmin) return children;

  return (
    <>
      <a className="pr-skip-link" href="#main-content" data-testid="skip-to-content-link">Skip to content</a>
      <Header />
      <SEOHead />
      <StructuredData />
      <main id="main-content" className="main-content min-h-screen">
        <PageTransition>
          <Suspense fallback={skeleton}>
            {children}
          </Suspense>
        </PageTransition>
      </main>
      <Footer />
      <QuickQuoteDrawer />
      <BackToTop />
      <WhatsAppWidget />
      <AuraChat />
    </>
  );
}

// ============================================
// APP ROUTES
// ============================================
function AppRoutes() {
  return (
    <>
      <ScrollToTop />
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<PublicLayout><HomePage /></PublicLayout>} />
        <Route path="/design-system" element={<PublicLayout><DesignSystemPage /></PublicLayout>} />
        
        {/* Services */}
        <Route path="/services" element={<PublicLayout skeleton={<ServiceSkeleton />}><ServicesPage /></PublicLayout>} />
        <Route path="/services/turnkey-fitout" element={<PublicLayout skeleton={<ServiceSkeleton />}><ServicePillarPage /></PublicLayout>} />
        <Route path="/services/bespoke-joinery" element={<PublicLayout skeleton={<ServiceSkeleton />}><ServicePillarPage /></PublicLayout>} />
        <Route path="/services/custom-structures" element={<PublicLayout skeleton={<ServiceSkeleton />}><ServicePillarPage /></PublicLayout>} />
        <Route path="/services/refurbishment-renovation" element={<PublicLayout skeleton={<ServiceSkeleton />}><ServicePillarPage /></PublicLayout>} />
        <Route path="/services/:slug" element={<PublicLayout skeleton={<ServiceSkeleton />}><ServiceDetailPage /></PublicLayout>} />
        
        {/* Sectors */}
        <Route path="/sectors" element={<PublicLayout><SectorsPage /></PublicLayout>} />
        <Route path="/sectors/:slug" element={<PublicLayout><SectorDetailPage /></PublicLayout>} />
        
        {/* Projects */}
        <Route path="/projects" element={<PublicLayout skeleton={<ProjectsSkeleton />}><ProjectsPage /></PublicLayout>} />
        <Route path="/projects/:slug" element={<PublicLayout><ProjectDetailPage /></PublicLayout>} />
        <Route path="/project-stories" element={<PublicLayout><ProjectStoriesPage /></PublicLayout>} />
        
        {/* Core Pages */}
        <Route path="/how-we-work" element={<PublicLayout><HowWeWorkPage /></PublicLayout>} />
        <Route path="/about" element={<PublicLayout><AboutPage /></PublicLayout>} />
        <Route path="/contact" element={<PublicLayout skeleton={<ContactSkeleton />}><ContactPage /></PublicLayout>} />
        <Route path="/request-quote" element={<PublicLayout><RequestQuotePage /></PublicLayout>} />
        <Route path="/resources" element={<PublicLayout><ResourcesPage /></PublicLayout>} />
        <Route path="/professionals" element={<PublicLayout><ProfessionalsPage /></PublicLayout>} />
        <Route path="/professionals/resources" element={<PublicLayout><ProfessionalsResourcesPage /></PublicLayout>} />
        <Route path="/professionals/project-tools" element={<PublicLayout><ProfessionalsToolsPage /></PublicLayout>} />
        <Route path="/professionals/request-materials" element={<PublicLayout><ProfessionalSubmissionPage /></PublicLayout>} />
        <Route path="/professionals/submit-boq" element={<PublicLayout><ProfessionalSubmissionPage /></PublicLayout>} />
        <Route path="/professionals/send-drawings" element={<PublicLayout><ProfessionalSubmissionPage /></PublicLayout>} />
        
        {/* Factory & Capabilities */}
        <Route path="/our-factory" element={<PublicLayout><OurFactoryPage /></PublicLayout>} />
        <Route path="/factory" element={<PublicLayout><FactoryPage /></PublicLayout>} />
        <Route path="/fabrication-capabilities" element={<PublicLayout><FabricationCapabilitiesPage /></PublicLayout>} />
        <Route path="/materials" element={<PublicLayout><MaterialsPage /></PublicLayout>} />
        <Route path="/capabilities" element={<PublicLayout><CapabilitiesPage /></PublicLayout>} />
        <Route path="/capabilities/furnishing-lifestyle" element={<PublicLayout><FurnishingLifestylePage /></PublicLayout>} />
        <Route path="/capabilities/:slug" element={<PublicLayout><CapabilityPage /></PublicLayout>} />
        <Route path="/bespoke-joinery" element={<PublicLayout><BespokeJoineryPage /></PublicLayout>} />
        
        {/* Industries */}
        <Route path="/industries" element={<PublicLayout><IndustriesPage /></PublicLayout>} />
        <Route path="/industries/:slug" element={<IndustryRedirect />} />
        
        {/* Applications */}
        <Route path="/residential-joinery" element={<PublicLayout><ApplicationPage /></PublicLayout>} />
        <Route path="/developer-mockups" element={<PublicLayout><ApplicationPage /></PublicLayout>} />
        <Route path="/retail-displays" element={<PublicLayout><ApplicationPage /></PublicLayout>} />
        <Route path="/kiosks-podiums" element={<PublicLayout><ApplicationPage /></PublicLayout>} />
        <Route path="/exhibition-stands" element={<PublicLayout><ApplicationPage /></PublicLayout>} />
        <Route path="/event-installations" element={<PublicLayout><ApplicationPage /></PublicLayout>} />
        <Route path="/office-renovation" element={<PublicLayout><ApplicationPage /></PublicLayout>} />
        <Route path="/retail-refurbishment" element={<PublicLayout><ApplicationPage /></PublicLayout>} />
        <Route path="/restaurant-renovation" element={<PublicLayout><ApplicationPage /></PublicLayout>} />
        <Route path="/villa-renovation" element={<PublicLayout><ApplicationPage /></PublicLayout>} />
        
        {/* Lead Generation Pages */}
        <Route path="/for/architects-interior-consultants" element={<PublicLayout><ForArchitectsPage /></PublicLayout>} />
        <Route path="/for/pmc-main-contractors" element={<PublicLayout><ForContractorsPage /></PublicLayout>} />
        <Route path="/for/retail-brands-franchise-owners" element={<PublicLayout><ForRetailBrandsPage /></PublicLayout>} />
        
        {/* Trust & SEO Pages */}
        <Route path="/areas-we-serve" element={<PublicLayout><AreasWeServePage /></PublicLayout>} />
        <Route path="/quality-hse" element={<PublicLayout><QualityHSEPage /></PublicLayout>} />
        <Route path="/aftercare-warranty" element={<PublicLayout><AfterCarePage /></PublicLayout>} />
        <Route path="/authority-approvals" element={<PublicLayout><AuthorityApprovalsPage /></PublicLayout>} />
        <Route path="/why-prastara" element={<Navigate to="/about" replace />} />
        <Route path="/clients" element={<Navigate to="/projects" replace />} />
        
        {/* Content Pages */}
        <Route path="/insights" element={<PublicLayout><InsightsPage /></PublicLayout>} />
        <Route path="/insights/:slug" element={<PublicLayout><InsightArticlePage /></PublicLayout>} />
        
        {/* Utility Pages */}
        <Route path="/vendor-registration" element={<PublicLayout><VendorRegistration /></PublicLayout>} />
        <Route path="/joinery/:slug" element={<PublicLayout><JoinerySubPage /></PublicLayout>} />
        <Route path="/prastara-os" element={<PublicLayout><PrastaraOS /></PublicLayout>} />
        <Route path="/tools" element={<PublicLayout><ToolsPage /></PublicLayout>} />
        <Route path="/budget-calculator" element={<PublicLayout><ToolsPage /></PublicLayout>} />
        
        {/* Legal */}
        <Route path="/privacy-policy" element={<PublicLayout><PrivacyPolicyPage /></PublicLayout>} />
        <Route path="/terms" element={<PublicLayout><TermsPage /></PublicLayout>} />

        {/* Admin Routes */}
        <Route path="/admin/login" element={<Suspense fallback={<PageSkeleton />}><AdminLogin /></Suspense>} />
        <Route path="/admin" element={<ProtectedRoute><Suspense fallback={<PageSkeleton />}><AdminLayout /></Suspense></ProtectedRoute>}>
          <Route index element={<Suspense fallback={<PageSkeleton />}><AdminDashboard /></Suspense>} />
          <Route path="projects" element={<Suspense fallback={<PageSkeleton />}><AdminProjects /></Suspense>} />
          <Route path="services" element={<Suspense fallback={<PageSkeleton />}><AdminServices /></Suspense>} />
          <Route path="sectors" element={<Suspense fallback={<PageSkeleton />}><AdminSectors /></Suspense>} />
          <Route path="inquiries" element={<Suspense fallback={<PageSkeleton />}><AdminInquiries /></Suspense>} />
          <Route path="testimonials" element={<Suspense fallback={<PageSkeleton />}><AdminTestimonials /></Suspense>} />
          <Route path="team" element={<Suspense fallback={<PageSkeleton />}><AdminTeam /></Suspense>} />
          <Route path="resources" element={<Suspense fallback={<PageSkeleton />}><AdminResources /></Suspense>} />
          <Route path="materials" element={<Suspense fallback={<PageSkeleton />}><AdminMaterials /></Suspense>} />
          <Route path="blog" element={<Suspense fallback={<PageSkeleton />}><AdminBlog /></Suspense>} />
          <Route path="before-after" element={<Suspense fallback={<PageSkeleton />}><AdminBeforeAfter /></Suspense>} />
          <Route path="project-stories" element={<Suspense fallback={<PageSkeleton />}><AdminProjectStories /></Suspense>} />
          <Route path="google-reviews" element={<Suspense fallback={<PageSkeleton />}><AdminGoogleReviews /></Suspense>} />
          <Route path="seo" element={<Suspense fallback={<PageSkeleton />}><AdminSEO /></Suspense>} />
          <Route path="images" element={<Suspense fallback={<PageSkeleton />}><AdminImages /></Suspense>} />
          <Route path="activity-log" element={<Suspense fallback={<PageSkeleton />}><AdminActivityLog /></Suspense>} />
          <Route path="settings" element={<Suspense fallback={<PageSkeleton />}><AdminSettings /></Suspense>} />
        </Route>

        {/* Catch-all */}
        {/* Bare parent redirects — prevent soft-404s on /joinery and /for */}
        <Route path="/joinery" element={<Navigate to="/services/bespoke-joinery" replace />} />
        <Route path="/for" element={<Navigate to="/about" replace />} />

        {/* 404 catch-all with noindex meta */}
        <Route path="*" element={<PublicLayout><Suspense fallback={<PageSkeleton />}><NotFound /></Suspense></PublicLayout>} />
      </Routes>
    </>
  );
}

// ============================================
// MAIN APP COMPONENT
// ============================================
function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <QuoteDrawerProvider>
            <BlueprintProvider>
              <SettingsProvider>
                <AppRoutes />
                <CookieBanner />
                <Toaster position="top-right" />
              </SettingsProvider>
            </BlueprintProvider>
          </QuoteDrawerProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
